import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { MainLayout } from "@/components/layout/MainLayout";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobileCardView } from "@/components/ui/mobile-card-view";
import { Badge } from "@/components/ui/badge";
import { Phone, Clock, ArrowRight, PhoneIncoming } from "lucide-react";
import { EmptyState } from "@/components/EmptyState";

export default function CallsPage() {
  useEffect(() => {
    document.title = 'Calls | Calie.ai';
  }, []);

  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const { data: calls } = useQuery({
    queryKey: ['calls'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('calie_calls')
        .select('*')
        .order('started_at', { ascending: false });
      if (error) throw error;
      return data || [];
    }
  });

  const { data: ads } = useQuery({
    queryKey: ['ads-for-attribution'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('calie_ads')
        .select('id, name');
      if (error) throw error;
      return data || [];
    }
  });

  const { data: campaigns } = useQuery({
    queryKey: ['campaigns-for-attribution'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('calie_campaigns')
        .select('campaign_id, name');
      if (error) throw error;
      return data || [];
    }
  });

  const adNameMap: Record<string, string> = Object.fromEntries((ads || []).map((a: any) => [a.id, a.name]));
  const campaignNameMap: Record<string, string> = Object.fromEntries((campaigns || []).map((c: any) => [c.campaign_id, c.name]));

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'failed': case 'no-answer': return 'bg-red-100 text-red-800';
      case 'busy': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const renderMobileCard = (call: any) => (
    <div 
      className="space-y-3 cursor-pointer active:bg-muted/50 -m-4 p-4 rounded-lg transition-colors"
      onClick={() => navigate(`/calls/${call.id}`)}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="space-y-1 min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="font-medium truncate">{call.from_number || 'Unknown'}</span>
          </div>
          <div className="text-sm text-muted-foreground truncate">
            → {call.to_number || '-'}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Badge className={`${getStatusColor(call.status)} text-xs`}>
            {call.status || 'Unknown'}
          </Badge>
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
        </div>
      </div>
      
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {typeof call.duration_sec === 'number' ? `${call.duration_sec}s` : '-'}
          </span>
          {call.campaign_id && campaignNameMap[call.campaign_id] && (
            <span className="truncate max-w-[120px]">{campaignNameMap[call.campaign_id]}</span>
          )}
        </div>
        <span className="text-xs">
          {call.started_at ? new Date(call.started_at).toLocaleDateString() : '-'}
        </span>
      </div>
    </div>
  );

  return (
    <MainLayout>
      <main className="space-y-4 sm:space-y-6">
        <header>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Calls</h1>
          <p className="text-sm text-muted-foreground">Recent calls for your workspace.</p>
        </header>
        
        <section>
          {isMobile ? (
            <MobileCardView
              data={(calls || []).map((c: any) => ({ ...c, id: c.id }))}
              renderCard={renderMobileCard}
              emptyState={<EmptyState icon={<PhoneIncoming className="h-8 w-8 text-muted-foreground" />} title="No calls yet" description="Calls will appear here once you receive them." />}
            />
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Started</TableHead>
                    <TableHead>From</TableHead>
                    <TableHead>To</TableHead>
                    <TableHead className="hidden lg:table-cell">Campaign</TableHead>
                    <TableHead className="hidden lg:table-cell">Ad</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Duration</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(calls || []).map((c: any) => (
                    <TableRow 
                      key={c.id} 
                      className="cursor-pointer hover:bg-muted/50" 
                      onClick={() => navigate(`/calls/${c.id}`)}
                    >
                      <TableCell className="whitespace-nowrap">
                        {c.started_at ? new Date(c.started_at).toLocaleString() : '-'}
                      </TableCell>
                      <TableCell className="max-w-[140px] truncate">{c.from_number || '-'}</TableCell>
                      <TableCell className="max-w-[140px] truncate">{c.to_number || '-'}</TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {c.campaign_id ? campaignNameMap[c.campaign_id] || c.campaign_id : '-'}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {c.ad_id ? adNameMap[c.ad_id] || c.ad_id : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(c.status)}>{c.status || '-'}</Badge>
                      </TableCell>
                      <TableCell>{typeof c.duration_sec === 'number' ? `${c.duration_sec}s` : '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </section>
      </main>
    </MainLayout>
  );
}
